import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, ShieldOff, Trash2, Globe, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";

interface DeviceBrowserBlacklistContentProps {
  deviceId: string;
}

export const DeviceBrowserBlacklistContent = ({ deviceId }: DeviceBrowserBlacklistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  // Fetch device details and browser config
  const deviceData = useConvexQuery(api.devices.get.details, { device: deviceId as any });
  const deviceBrowserConfig = useConvexQuery(
    api.devices.policies.get,
    deviceData ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );

  const updateDeviceBrowser = useConvexMutation(api.devices.policies.update);

  // Get current blacklisted sites - all URLs in blocklist are blocked
  const currentBlacklistedSites = deviceBrowserConfig ? 
    Object.keys(deviceBrowserConfig.blocklist || {}) : [];

  // Get current whitelisted sites - all URLs in allowlist are allowed
  const currentWhitelistedSites = deviceBrowserConfig ? 
    Object.keys(deviceBrowserConfig.allowlist || {}) : [];

  // Debug logging for API response
  useEffect(() => {
    if (deviceBrowserConfig) {
      console.log("=== BLACKLIST API RESPONSE DEBUG ===");
      console.log("Raw api.devices.policies.get response:", deviceBrowserConfig);
      console.log("Response type:", typeof deviceBrowserConfig);
      console.log("Response keys:", Object.keys(deviceBrowserConfig));
      console.log("=== END BLACKLIST API RESPONSE DEBUG ===");
      
      console.log("=== BLACKLIST DATA STRUCTURE DEBUG ===");
      console.log("deviceBrowserConfig?.blocklist:", deviceBrowserConfig?.blocklist);
      console.log("Type of blocklist:", typeof deviceBrowserConfig?.blocklist);
      console.log("Is blocklist an array?", Array.isArray(deviceBrowserConfig?.blocklist));
      console.log("Object.keys result:", Object.keys(deviceBrowserConfig.blocklist || {}));
      console.log("currentBlacklistedSites:", currentBlacklistedSites);
      console.log("=== END BLACKLIST DEBUG ===");
    }
  }, [deviceBrowserConfig, currentBlacklistedSites]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: "Error message copied to clipboard",
      });
    } catch (err) {
      console.error("Clipboard copy failed:", err);
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (currentWhitelistedSites.includes(newUrl)) {
      toast({
        title: "Cannot Block URL",
        description: `${newUrl} is currently in the whitelist. Please remove it from the whitelist first.`,
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      console.log("=== ADD URL TO BLACKLIST DEBUG ===");
      console.log("Current blacklisted sites before adding:", currentBlacklistedSites);
      console.log("URL to add:", newUrl);
      
      const newBlacklistedUrls = [...currentBlacklistedSites, newUrl];
      console.log("New array for mutation:", newBlacklistedUrls);
      console.log("Mutation parameters:", {
        device: deviceId,
        blockedUrls: newBlacklistedUrls,
      });

      await updateDeviceBrowser({
        device: deviceId as any,
        blockedUrls: newBlacklistedUrls,
      });

      console.log("Mutation succeeded");
      console.log("=== END ADD URL DEBUG ===");

      toast({
        title: "URL Blocked",
        description: `${newUrl} has been added to the blacklist.`,
      });
      setNewUrl("");
      
      // Force a small delay to ensure Convex query has time to update
      setTimeout(() => setIsUpdating(false), 100);
    } catch (error: any) {
      console.error("Error adding URL to blacklist:", error);
      const msg = `Failed to add URL to blacklist: ${error.message || "Unknown error"}`;
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{msg}</span>
            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(msg)} className="h-6 w-6 p-0">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
      setIsUpdating(false);
    }
  };

  const handleRemoveUrl = async (url: string) => {
    if (!deviceData?.device.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsUpdating(true);
    try {
      console.log("=== REMOVE URL FROM BLACKLIST DEBUG ===");
      console.log("URL to remove:", url);
      console.log("Current blacklisted sites before removal:", currentBlacklistedSites);
      
      const newBlacklistedUrls = currentBlacklistedSites.filter((u) => u !== url);
      console.log("New array after removal:", newBlacklistedUrls);
      console.log("Mutation parameters:", {
        device: deviceId,
        blockedUrls: newBlacklistedUrls,
      });

      await updateDeviceBrowser({
        device: deviceId as any,
        blockedUrls: newBlacklistedUrls,
      });

      console.log("Mutation succeeded");
      console.log("=== END REMOVE URL DEBUG ===");

      toast({
        title: "URL Unblocked",
        description: `${url} has been removed from the blacklist.`,
      });
      
      // Force a small delay to ensure Convex query has time to update
      setTimeout(() => setIsUpdating(false), 100);
    } catch (error) {
      console.error("Error removing URL from blacklist:", error);
      const msg = `Failed to remove URL from blacklist: ${error?.message || "Please try again."}`;
      toast({
        title: "Error",
        description: (
          <div className="flex items-center justify-between gap-2">
            <span className="flex-1">{msg}</span>
            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(msg)} className="h-6 w-6 p-0">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        ),
        variant: "destructive",
      });
      setIsUpdating(false);
    }
  };

  const filteredSites = currentBlacklistedSites.filter((url) =>
    url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!deviceData || !deviceBrowserConfig) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Browser Blacklist</h3>
        <p className="text-gray-600">Websites explicitly blocked for this device</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add Website to Blacklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter website URL (e.g., example.com)"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleAddUrl} variant="destructive" disabled={isUpdating}>
              <Plus className="w-4 h-4 mr-1" />
              {isUpdating ? "Adding..." : "Block"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search blacklisted websites..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div>
        <h4 className="font-medium mb-3">Current Browser Blacklist</h4>
        {filteredSites.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <ShieldOff className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {searchQuery ? "No websites found matching your search" : "No websites in blacklist"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredSites.map((url, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-sm">{url}</p>
                    <p className="text-xs text-gray-500">Website URL</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">Blocked</Badge>
                  <Button size="sm" variant="outline" onClick={() => handleRemoveUrl(url)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
